"use client";

import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";

import { useAuth } from "@/lib/auth";
import { setAuthTelemetryReporter } from "@/lib/platform-fetch";

/**
 * Datadog RUM — page loads, Core Web Vitals, JS errors, and Session Replay, stitched to the
 * backend traces the platform API produces (DESIGN.md 5.14).
 *
 * **These three values are hardcoded on purpose.** The RUM `clientToken` is a *public* credential
 * — unlike a Datadog API key it is designed to be shipped inside browser JavaScript, it can only
 * submit RUM data, and it is the same value across every environment. Putting it in an env var
 * would imply a secrecy it does not have and add build-time config for nothing. Environments are
 * separated by the `env` tag below, not by the token.
 *
 * **Privacy: `defaultPrivacyLevel: "mask"`.** This is a chat UI, so the DOM contains conversation
 * content, and Session Replay records the DOM. `mask` replaces all text nodes and input values
 * with blocks before anything leaves the browser, so replays show layout and interaction without
 * content. That keeps this consistent with the platform-wide rule that conversation content does
 * not reach Datadog — G13 (LLM Observability content capture) stays off independently, and
 * reasoning traces and transcripts still live only in S3.
 *
 * Anyone tempted to relax this, or to add `defaultPrivacyLevel: "allow"` on a subtree, is making a
 * CPNI decision and needs Infosec sign-off, not a config tweak.
 */
/**
 * The `env` tag, read from build-time config.
 *
 * Environments share one client token, so `env` is the only thing separating their RUM data.
 *
 * **This docstring used to say "derived from the hostname rather than from build-time config"**
 * and justified it with the Amplify `deploy-dev` branch subdomain. Both the mechanism and the
 * justification are gone — see the body for why the hostname approach was actively unsafe once
 * the app stopped living at a hostname we control the shape of.
 */
function datadogEnv(): string {
  // **Was derived from the hostname, which was an Amplify assumption and a silent one.** It
  // returned "dev" only for a host starting `deploy-dev.` -- the Amplify branch subdomain -- so
  // the moment the app moved to any other hostname every dev session would have been tagged
  // `env:prod` and quietly polluted production RUM. Nothing would have errored.
  //
  // The environment is known at build time and is already inlined for other purposes, so read it
  // rather than infer it from a URL that is no longer ours to predict.
  return import.meta.env.VITE_AIP_ENVIRONMENT || "local";
}

/**
 * The `version` tag — the third leg of Datadog's unified service tagging, alongside `env` and
 * `service`.
 *
 * **Was absent entirely, which is not something the SPA migration broke — it was never set.**
 * Without it the Version facet is empty, so RUM cannot be split by deploy: no "error rate on the
 * build I just shipped versus the one before", and Datadog's release tracking has nothing to key
 * on. That is the single most useful pivot after `env`, and it costs one build-time string.
 *
 * `git describe --tags --always --dirty` rather than `package.json`'s version, which is `0.0.0`
 * and has never been bumped — a version that never changes is worse than none, because the facet
 * looks populated. `describe` maps a RUM session to a release tag, or to an exact commit when
 * the build is past one, which is what you actually want when a regression appears.
 *
 * **Was the 7-char SHA alone**, until the sidebar started showing a release tag
 * (`components/release-version.tsx`) and the two would have needed separate variables. They read
 * the same one, so the facet and the corner of the screen can never disagree. `docs/RELEASE.md`
 * ("Runtime stamping") warned that this changes the shape of the `version` facet mid-stream:
 * sessions before this release are `abc1234`, sessions after are `v0.84.0`.
 *
 * Falls back to `"local"` (matching `datadogEnv`) rather than to `undefined`: a local
 * `npm run build` supplies nothing, and grouping every developer build under one honest label
 * beats a literal `"undefined"` string in the facet.
 */
function datadogVersion(): string {
  return import.meta.env.VITE_APP_VERSION || "local";
}

/**
 * Initialises RUM. Called from `src/main.tsx` **before** `createRoot`, not from a component.
 *
 * This was a `<DatadogRum />` component mounted in the root layout while the app was Next, and
 * the port back is deliberately not like-for-like. `init` is what monkey-patches `window.fetch`,
 * and `AuthProvider`'s `/v1/me` is the first request the app makes — fired by `useQuery` as soon
 * as React commits. From inside a `useEffect` there is no ordering guarantee between the two, so
 * whether the session's first and most diagnostically useful request carries `traceparent` would
 * depend on where the tag happened to sit among its siblings. Calling it before React starts
 * makes it unconditional.
 *
 * It also means a JS error thrown during `createRouter`, route-tree evaluation or first render is
 * captured. From an effect those happen before the handler is installed, so a boot failure
 * produces *zero* RUM events — indistinguishable from the SDK not being wired up at all, which
 * is exactly the bug this file spent a week in.
 */
export function initDatadogRum() {
  // No longer guarding a Fast Refresh double-invoke — there is no effect to re-run. Kept because
  // `init` is not idempotent and warns loudly on a repeat call: a second call site added later is
  // now a no-op instead of a replaced session.
  if (datadogRum.getInitConfiguration() !== undefined) return;

  datadogRum.init({
    applicationId: "eb80da45-307b-4d12-b9e6-d873fa987f49",
    clientToken: "pub30994335e45d7078303918239e47b793",
    site: "datadoghq.com",
    service: "ai-platform-web",
    env: datadogEnv(),
    version: datadogVersion(),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: "mask",
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    // What links a browser session to the backend trace: the SDK patches `fetch`/XHR and
    // injects `traceparent` on requests to this origin, so `apps/web/src/lib/platform-fetch.ts`
    // needs no change at all — injection happens beneath it.
    //
    // `propagatorTypes: ["tracecontext"]` explicitly, rather than the SDK default of
    // `["tracecontext", "datadog"]`: the `datadog` propagator adds four `x-datadog-*` headers,
    // each of which would need adding to the API's CORS allow-list
    // (`packages/api-common/src/api_common/cors.py`, `ALLOWED_HEADERS`) for zero benefit — our
    // backend is pure OTEL and reads W3C. Non-safelisted request headers
    // that are not allow-listed fail the *preflight*, i.e. they break the request outright
    // rather than just losing the trace.
    allowedTracingUrls: [
      {
        // Same-origin now: the Worker proxies `/api/*`, so the API is reached at our own
        // origin rather than an `execute-api` hostname. That also defuses the CORS hazard the
        // comment above describes -- a same-origin request has no preflight to fail -- but the
        // propagator list stays `tracecontext` only, because the backend is pure OTEL and the
        // `x-datadog-*` headers would still buy nothing.
        match: (url: string) =>
          url.startsWith(`${window.location.origin}/api`),
        propagatorTypes: ["tracecontext"],
      },
    ],
    traceSampleRate: 100,
    // Only attach trace context to requests RUM actually sampled, so the backend never records
    // a parent span id that has no corresponding RUM event.
    traceContextInjection: "sampled",
    // Set explicitly even though `true` is already the SDK default, because relying on that
    // default is what took the API down on 2026-08-27: `baggage` (carrying `session.id` and
    // `user.id`) is sent on every traced request, and it was missing from the API's CORS
    // allow-list, so *every* call failed at the preflight. Stating it here keeps the set of
    // headers we emit visible next to the code, and pins it so an SDK upgrade flipping the
    // default cannot silently change what crosses the wire.
    //
    // Note it carries `session.id` and `user.id` only — **never the email**. `usr.email` reaches
    // the backend's spans from the backend's own identity resolution
    // (`packages/observability/attributes.py`), not from here, so the two sides are independent
    // and `usr.id` is the only value that has to match.
    //
    // Any change to this line, or to `propagatorTypes` above, must be matched in
    // `packages/api-common/src/api_common/cors.py`'s `ALLOWED_HEADERS` — **not** in
    // `infra/sam/template.yaml`, which this comment used to name and which has held no CORS
    // configuration since the REST migration (API Gateway REST does not stamp CORS onto real
    // responses; Powertools does it in-Lambda). `apps/web/tests/rum-contract.test.mjs` now
    // asserts that coupling in this direction, and
    // `apps/api/ingress/tests/test_cors_allow_headers.py` asserts it from the API side.
    propagateTraceBaggage: true,
  });

  datadogRum.startSessionReplayRecording();

  // Makes the auth path's own account of itself visible in RUM.
  //
  // **This exists because RUM alone could not answer "did the refresh even run?"** The
  // refresh-and-retry in `platform-fetch.ts` is silent by design, and RUM sees only what reaches
  // the network -- so a `POST /v1/auth/refresh` that fails before the wire leaves no resource
  // event, and is indistinguishable from a refresh that was never attempted. Over the 30 days
  // before this was added, RUM held exactly one refresh resource (and that one never completed)
  // against a dozen credential rejections, and there was no way to tell which of the two stories
  // that was. `platform-fetch.ts` now reports each branch; this hands those reports to RUM.
  //
  // `addAction` rather than `addError`: a silent recovery is the *expected* outcome here and the
  // one most worth being able to count, so none of these are errors. `step` becomes the action
  // name -- a small closed set, so the actions stay groupable -- and the rest becomes context.
  setAuthTelemetryReporter(({ step, ...context }) => {
    datadogRum.addAction(`auth.${step}`, context);
  });
}

/**
 * A client-side recovery that should never have been needed -- the code worked around a bug
 * rather than failing the user, and this is what keeps the workaround from hiding it. An error
 * rather than an action for that reason. Lives here so this module stays the only importer of
 * the SDK (`tests/rum-contract.test.mjs`); a no-op wherever RUM was never initialised.
 */
export function reportMaskedBug(message: string, context: Record<string, unknown>) {
  if (datadogRum.getInitConfiguration() === undefined) return;
  datadogRum.addError(new Error(message), context);
}

/**
 * Associates the current RUM session with the signed-in user. Mounted once, in
 * `src/routes/__root.tsx`, inside `AuthProvider`.
 *
 * **It reads identity itself rather than taking props, and that is the fix for a real bug.** It
 * used to take `{ userId, email, name }` because the Next app's pattern was a server component
 * reading the session and passing values down — so the only place that could mount it was an
 * already-authenticated page, and in practice that meant `app/page.tsx` alone. Every `/admin/*`
 * route and `/settings/integrations` therefore produced RUM sessions with no user attached, and
 * nothing reported it. Reading `useAuth()` here means a route cannot be forgotten: the root route
 * wraps all of them.
 *
 * It must stay *inside* `AuthProvider` (`useAuth` throws otherwise, taking every route with it)
 * and must stay *outside* `RequireAuth` — `/setup` is deliberately ungated, because it exists for
 * the state where nobody can sign in yet.
 *
 * `setUser` applies to the ongoing RUM session rather than to a single view, so one call covers
 * the rest of that browser session's navigation.
 *
 * Shares the backend's `id` and `email` exactly (`packages/observability/attributes.py`,
 * docs/DECISIONS.md G54). The `id` half is the part that must not drift:
 *
 * - `id` is the **opaque platform user id**, and it is what joins a RUM session to its backend
 *   traces. Both sides have to send the identical value. This used to be the email, which worked
 *   only because the backend's `usr.id` was also the email; once that became a platform UUID,
 *   sending the email here would have broken every RUM-to-trace join with no error, no log and no
 *   failing test — which is exactly the kind of breakage that gets discovered months later.
 * - `email` is the readable half, so a RUM session is identifiable without resolving a UUID.
 * - `name` is **deliberately unlike the backend**, which resolves `usr.name` email-first
 *   (`attributes.py`: `email or display_name or user_id`) and so normally puts the address there
 *   too. Datadog labels RUM sessions by `usr.name`, so this prefers the display name — "Tyler
 *   Sorensen" reads better in the session list than the email repeated in two columns, and
 *   `usr.email` carries the address either way. This divergence is intentional; do not "fix" it
 *   to match the backend without also deciding you prefer the email as the session label.
 */
export function DatadogRumUser() {
  const { user } = useAuth();

  useEffect(() => {
    // `undefined` is "/v1/me is still in flight", not "signed out" -- say nothing rather than
    // clearing a user we are about to set. This also covers the SESSION_EXPIRED path, where
    // `queryClient.clear()` returns the query to pending for the instant before `startSignIn()`
    // navigates away.
    if (user === undefined) return;

    if (user === null) {
      // Signed out, or never signed in. `setUser` is scoped to the RUM *session*, which outlives
      // a client-side navigation -- so without this, the next person to use a shared in-store
      // device inherits the previous user's attribution (DESIGN.md 5.11 is explicit that those
      // devices are shared; `tests/logout-purge.test.mjs` exists for the same hazard). A no-op
      // when nothing was ever set, so `/setup` pays nothing for it.
      datadogRum.clearUser();
      return;
    }

    // `setUser`'s signature requires a non-empty `id`, and a malformed `/v1/me` must not send
    // `id: undefined` into it. `"unknown"` was the old prop-level sentinel; kept because a
    // literal "unknown" in the join key is worse than an unattributed session.
    if (!user.user_id || user.user_id === "unknown") return;

    datadogRum.setUser({
      id: user.user_id,
      // `||` rather than `??`, which is all the types require (`Me.email` is `string | null`,
      // the SDK wants `string | undefined`): `||` also collapses `""`. An empty-string email
      // from a directory row is a real shape here, and `usr.email: ""` is worse than absent --
      // it matches `@usr.email:*` and pollutes every "sessions with a known user" filter.
      email: user.email || undefined,
      name: user.name || user.email || user.user_id,
    });
    // `user` alone is a sufficient dep: `AuthProvider` rebuilds its context object every render,
    // but this field is `isPending ? undefined : (data ?? null)`, and TanStack Query's `data` is
    // referentially stable across renders via structural sharing.
  }, [user]);

  return null;
}
